import React, { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMyArticleFeedback, useSubmitArticleFeedback } from "../../hooks/useKnowledgeBase";

interface ArticleFeedbackPromptProps {
  articleId: string;
}

// An anonymous visitor's vote isn't theirs to look up on the server, so this browser remembers it to show the thanks
const storageKey = (articleId: string) => `kbFeedback_${articleId}`;

function readRemembered(articleId: string): boolean | null {
  try {
    const value = localStorage.getItem(storageKey(articleId));
    return value === "helpful" ? true : value === "not-helpful" ? false : null;
  } catch {
    return null;
  }
}

/**
 * "Was this article helpful?" A Yes is sent at once. A No first asks what was missing, then sends the vote with the
 * comment (or without one, with Skip) - one submission, so the comment isn't lost and the author hears about it once.
 * A signed-in customer's earlier vote comes from the server; an anonymous visitor's from this browser.
 */
export const ArticleFeedbackPrompt: React.FC<ArticleFeedbackPromptProps> = ({ articleId }) => {
  const submitFeedback = useSubmitArticleFeedback();
  const { data: myFeedback } = useMyArticleFeedback(articleId);
  const [remembered, setRemembered] = useState<boolean | null>(() => readRemembered(articleId));
  const [askingWhy, setAskingWhy] = useState(false);
  const [comment, setComment] = useState("");

  const submit = (isHelpful: boolean, withComment?: string) => {
    submitFeedback.mutate(
      { articleId, request: { isHelpful, comment: withComment?.trim() || undefined } },
      {
        onSuccess: () => {
          try {
            localStorage.setItem(storageKey(articleId), isHelpful ? "helpful" : "not-helpful");
          } catch {
            // Storage unavailable (private mode): the thanks shows for this visit only
          }
          setRemembered(isHelpful);
          setAskingWhy(false);
        },
      }
    );
  };

  const answered = myFeedback ? myFeedback.isHelpful : remembered;

  if (answered !== null && answered !== undefined) {
    return (
      <div className="text-center py-4 space-y-1" role="status">
        <p className="text-sm text-muted-foreground">
          Thanks for your feedback! You found this article {answered ? "helpful" : "not helpful"}.
        </p>
        {/* It didn't answer their question: point to the way to someone who can (the article page's "Still need help?") */}
        {!answered && (
          <p className="text-sm text-foreground">Sorry it didn't help - submit a ticket below and our support team will take it from here.</p>
        )}
      </div>
    );
  }

  return (
    <div className="text-center space-y-3">
      <p className="text-sm font-medium text-foreground" id={`feedback-question-${articleId}`}>
        Was this article helpful?
      </p>
      {!askingWhy ? (
        <div className="flex items-center justify-center gap-3" role="group" aria-labelledby={`feedback-question-${articleId}`}>
          <Button variant="outline" size="sm" onClick={() => submit(true)} disabled={submitFeedback.isPending} className="gap-2">
            <ThumbsUp className="h-4 w-4" aria-hidden="true" />
            Yes
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAskingWhy(true)} disabled={submitFeedback.isPending} className="gap-2">
            <ThumbsDown className="h-4 w-4" aria-hidden="true" />
            No
          </Button>
        </div>
      ) : (
        <div className="max-w-md mx-auto space-y-2">
          <label htmlFor={`feedback-comment-${articleId}`} className="block text-sm text-muted-foreground">
            What was missing or unclear? (optional)
          </label>
          <textarea
            id={`feedback-comment-${articleId}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={3}
            autoFocus
            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
          <div className="flex items-center justify-center gap-2">
            <Button size="sm" onClick={() => submit(false, comment)} disabled={submitFeedback.isPending || !comment.trim()}>
              Send feedback
            </Button>
            <Button size="sm" variant="ghost" onClick={() => submit(false)} disabled={submitFeedback.isPending}>
              Skip
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
